import type { ChakraProps } from '@chakra-ui/react';
import { FormControl, Input, InputGroup, InputRightElement, Textarea, chakra, shouldForwardProp } from '@chakra-ui/react';
import React from 'react';
import type { FieldValues, Path } from 'react-hook-form';
import { useController, useFormContext } from 'react-hook-form';

import type { FormFieldPropsBase } from './types';

import FieldError from '../components/FieldError';
import FormInputPlaceholder from '../inputs/FormInputPlaceholder';

interface Props<
  FormFields extends FieldValues,
  Name extends Path<FormFields> = Path<FormFields>,
> extends FormFieldPropsBase<FormFields, Name> {
  asComponent?: 'Input' | 'Textarea';
  inputSx?: ChakraProps['sx'];
}

const FormFieldText = <
  FormFields extends FieldValues,
  Name extends Path<FormFields> = Path<FormFields>,
>({
  name,
  placeholder,
  isReadOnly,
  isRequired,
  rules,
  onBlur,
  type = 'text',
  rightElement,
  ariaLabel,
  min,
  asComponent,
  max,
  step,

  className,
  size = 'md',
  bgColor,
  minH,
  maxH,
  inputSx,
}: Props<FormFields, Name>) => {
  const { control } = useFormContext<FormFields>();
  const { field, fieldState, formState } = useController<FormFields, typeof name>({
    control,
    name,
    rules: { ...rules, required: isRequired },
  });

  const isDisabled = formState.isSubmitting;
  const inputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  const handleBlur = React.useCallback(() => {
    field.onBlur();
    onBlur?.();
  }, [ field, onBlur ]);

  const Component = asComponent === 'Textarea' ? Textarea : Input;
  const input = (
    <Component
      { ...field }
      ref={ (node: HTMLInputElement | HTMLTextAreaElement | null) => {
        inputRef.current = node;
        field.ref(node);
      } }
      onBlur={ handleBlur }
      isInvalid={ Boolean(fieldState.error) }
      isDisabled={ isDisabled }
      isReadOnly={ isReadOnly }
      autoComplete="off"
      type={ type }
      aria-label={ ariaLabel }
      placeholder={ typeof placeholder === 'string' ? placeholder : ' ' }
      min={ min }
      max={ max }
      step={ step }
      size={ size }
      // bgColor={ bgColor }
      minH={ minH }
      maxH={ maxH }
      _placeholder={{
        color: 'form_input_placeholder',
      }}
      sx={{
        bgColor: 'form_input_bg',
        padding: '12px 16px !important',
        border: '1px solid',
        borderColor: 'form_input_border',
        color: 'form_input_fg',
        borderRadius: '8px',
        '&:focus, &:focus-visible': {
          borderColor: 'form_input_focus',
          boxShadow: '0 0 0 1px #14DCAC',
        },
        '&[aria-invalid=true]': {
          borderColor: 'error',
        },
        '&::-webkit-calendar-picker-indicator': {
          filter: 'invert(1)',
          opacity: 0.72,
          cursor: 'pointer',
        },
        ...inputSx,
      }}
    />
  );
  const inputPlaceholder = size !== 'xs' && <FormInputPlaceholder text={ placeholder } error={ fieldState.error }/>;

  return (
    <FormControl
      className={ className }
      variant="floating"
      isDisabled={ isDisabled }
      isRequired={ isRequired }
      size={ size }
      bgColor={ bgColor }
    >
      { rightElement ? (
        <InputGroup>
          { input }
          { inputPlaceholder }
          <InputRightElement h="100%"> { rightElement({ field, inputRef }) } </InputRightElement>
        </InputGroup>
      ) : (
        <>
          { input }
          { fieldState.error?.message && (
            <FieldError message={ fieldState.error.message } mt={ 2 }/>
          ) }
        </>
      ) }
    </FormControl>
  );
};

const WrappedFormFieldText = chakra(FormFieldText, {
  shouldForwardProp: (prop) => {
    const isChakraProp = !shouldForwardProp(prop);

    if (isChakraProp && ![ 'ariaLabel', 'bgColor', 'inputSx', 'maxH', 'max', 'min', 'minH', 'size', 'step' ].includes(prop)) {
      return false;
    }

    return true;
  },
});

export type WrappedComponent = <
  FormFields extends FieldValues,
  Name extends Path<FormFields> = Path<FormFields>,
>(props: Props<FormFields, Name> & ChakraProps) => React.JSX.Element;

export default React.memo(WrappedFormFieldText) as WrappedComponent;
